// ============================================
// components/AvalancheLayer.jsx
// ============================================

import React, { useState, useEffect } from 'react';
import { CircleMarker, Popup, Tooltip } from 'react-leaflet';
import '../styles/AvalancheLayer.css';

/**
 * Layer per visualizzare allerte valanghe su mappa nazionale
 * Uso: <AvalancheLayer minDangerLevel={2} showTooltips={true} />
 */
export default function AvalancheLayer({
    minDangerLevel = 1,      // Mostra solo zone con pericolo >= questo valore
    showTooltips = false,    // Mostra tooltip al hover
    clusterRadius = 25000,   // Raggio clustering (metri)
    onZoneClick = null       // Callback al click su zona
}) {
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAvalancheData();
    }, []);

    const fetchAvalancheData = async () => {
        setLoading(true);
        setError(null);

        try {
            const backendUrl = process.env.REACT_APP_API_URL_NODE;
            const response = await fetch(`${backendUrl}/api/avalanche-alerts/national/geojson`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.geojson) {
                setZones(data.geojson.features);
            } else {
                throw new Error('Invalid response format');
            }

        } catch (err) {
            console.error('Error fetching avalanche data:', err);
            setError('Impossibile caricare le allerte valanghe');
        } finally {
            setLoading(false);
        }
    };

    // Filtra zone in base a minDangerLevel
    const visibleZones = zones.filter(
        zone => zone.properties.dangerLevel >= minDangerLevel
    );

    if (loading) {
        return null; // Non mostrare nulla durante caricamento
    }

    if (error) {
        console.warn('AvalancheLayer error:', error);
        return null; // Fail silently per non bloccare la mappa
    }

    return (
        <>
            {visibleZones.map((zone, index) => {
                const { properties, geometry } = zone;
                const [lon, lat] = geometry.coordinates;

                // Radius basato su danger level (più grande = più pericoloso)
                const radius = 15 + (properties.dangerLevel * 5);

                return (
                    <CircleMarker
                        key={`avalanche-${properties.zoneCode}-${index}`}
                        center={[lat, lon]}
                        radius={radius}
                        pathOptions={{
                            fillColor: properties.dangerColor,
                            fillOpacity: 0.6,
                            color: darkenColor(properties.dangerColor),
                            weight: 2,
                            opacity: 0.9
                        }}
                        eventHandlers={{
                            click: () => {
                                if (onZoneClick) {
                                    onZoneClick(properties);
                                }
                            }
                        }}
                    >
                        {/* Tooltip al hover */}
                        {showTooltips && (
                            <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                                <div className="avalanche-tooltip">
                                    <strong>{properties.region}</strong>
                                    <div className="danger-badge" style={{ backgroundColor: properties.dangerColor }}>
                                        Pericolo: {properties.dangerLabel}
                                    </div>
                                </div>
                            </Tooltip>
                        )}

                        {/* Popup al click */}
                        <Popup maxWidth={300} className="avalanche-popup">
                            <div className="avalanche-popup-content">
                                <div className="popup-header">
                                    <h3>{properties.region}</h3>
                                    <div
                                        className="danger-level"
                                        style={{
                                            backgroundColor: properties.dangerColor,
                                            color: properties.dangerLevel >= 3 ? 'white' : 'black'
                                        }}
                                    >
                                        Livello {properties.dangerLevel}/5
                                    </div>
                                </div>

                                <div className="popup-body">
                                    <div className="info-row">
                                        <span className="label">Pericolo:</span>
                                        <strong>{properties.dangerLabel}</strong>
                                    </div>

                                    {properties.hasElevationDependency && properties.elevation && (
                                        <div className="info-row">
                                            <span className="label">Quota critica:</span>
                                            <strong>{properties.elevation}m e oltre</strong>
                                        </div>
                                    )}

                                    {properties.problems && (
                                        <div className="info-row">
                                            <span className="label">Problemi:</span>
                                            <span>{properties.problems}</span>
                                        </div>
                                    )}

                                    {properties.highlight && (
                                        <div className="highlight">
                                            {properties.highlight}
                                        </div>
                                    )}

                                    <div className="popup-footer">
                                        <small>
                                            Valido fino: {new Date(properties.validUntil).toLocaleDateString('it-IT')}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </Popup>
                    </CircleMarker>
                );
            })}
        </>
    );
}

/**
 * Utility: scurisce un colore per il bordo
 */
function darkenColor(color) {
    // Converti hex a RGB
    const hex = color.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Scurisci del 30%
    r = Math.floor(r * 0.7);
    g = Math.floor(g * 0.7);
    b = Math.floor(b * 0.7);

    // Converti back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

